import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { 
  Terminal, 
  Play, 
  Clock,
  Calendar,
  Database,
  FileText,
  Download,
  Settings,
  AlertCircle
} from 'lucide-react';

const Commands = () => {
  const [activeCommand, setActiveCommand] = useState('');
  const [commandOutput, setCommandOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [formData, setFormData] = useState({});

  const commandTemplates = {
    'sync-all': {
      title: 'Sync All Sources',
      description: 'Synchronize data from all configured sources',
      icon: Database,
      endpoint: '/bronze/sync/all',
      method: 'POST',
      fields: [
        { name: 'full_refresh', type: 'checkbox', label: 'Full Refresh' }
      ]
    },
    'sync-source': {
      title: 'Sync Specific Source',
      description: 'Synchronize data from a specific source',
      icon: Database,
      endpoint: '/bronze/sync/{source}/{data_type}',
      method: 'POST',
      fields: [
        { 
          name: 'source', 
          type: 'select', 
          label: 'Source', 
          options: ['servicetitan', 'adp', 'azuga'],
          required: true 
        },
        { name: 'data_type', type: 'text', label: 'Data Type (leave blank to sync every active type for the source)' },
        { name: 'tenant_id', type: 'text', label: 'Tenant ID (optional)' },
        { name: 'full_refresh', type: 'checkbox', label: 'Full Refresh' }
      ]
    },
    'historic-adp': {
      title: 'Historic ADP Time Cards',
      description: 'Sync historic ADP time cards for a date range',
      icon: Calendar,
      endpoint: '/bronze/sync/adp/time-cards/historic',
      method: 'POST',
      fields: [
        { name: 'start_date', type: 'date', label: 'Start Date', required: true },
        { name: 'end_date', type: 'date', label: 'End Date', required: true }
      ]
    },
    'azuga-breadcrumbs': {
      title: 'Azuga Breadcrumbs Range',
      description: 'Sync Azuga breadcrumbs for a specific date range',
      icon: Calendar,
      endpoint: '/bronze/sync/azuga/breadcrumbs/date-range',
      method: 'POST',
      fields: [
        { name: 'start_date', type: 'date', label: 'Start Date', required: true },
        { name: 'end_date', type: 'date', label: 'End Date', required: true }
      ]
    },
    'refresh-gold': {
      title: 'Refresh Gold Layer',
      description: 'Trigger a full refresh of the Gold layer analytics for a specific date range',
      icon: Settings,
      endpoint: '/gold/refresh',
      method: 'POST',
      fields: [
        { name: 'start_date', type: 'date', label: 'Start Date', required: true },
        { name: 'end_date', type: 'date', label: 'End Date (optional - defaults to start_date if not provided)', required: false }
      ]
    },
    'cleanup-sync': {
      title: 'Cleanup Sync Data',
      // Retention is a strategy, not a window: all historic range syncs are kept,
      // plus the most recent daily sync per target_date. There is no days knob to
      // expose -- SyncService.cleanup_sync_data_standalone takes a days_to_keep
      // and ignores it.
      description: 'Clean up old sync data and states (ADP time-cards only; keeps all historic syncs and the most recent daily sync per date)',
      icon: FileText,
      endpoint: '/bronze/cleanup/daily-syncs',
      method: 'POST',
      fields: []
    }
  };

  // Helper function to format date for API
  const formatDateForAPI = (date) => {
    return date ? date.toISOString().split('T')[0] : ''; // YYYY-MM-DD format
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeCommand) return;

    const command = commandTemplates[activeCommand];
    if (!command) return;

    setIsRunning(true);
    setCommandOutput('');

    try {
      // Format dates for API before sending
      const formattedData = { ...formData };
      command.fields.forEach(field => {
        if (field.type === 'date' && formattedData[field.name]) {
          formattedData[field.name] = formatDateForAPI(formattedData[field.name]);
        }
      });

      // Handle dynamic endpoint construction for sync-source command
      let endpoint = command.endpoint;
      if (activeCommand === 'sync-source') {
        const source = formattedData.source;
        const dataType = formattedData.data_type;
        
        if (dataType) {
          endpoint = `/api/bronze/sync/${source}/${dataType}`;
        } else {
          endpoint = `/api/bronze/sync/${source}`;
        }
        
        // Remove source and data_type from the data payload since they're in the URL
        delete formattedData.source;
        delete formattedData.data_type;
      }

      // Every command endpoint declares its arguments with FastAPI's Query(), and
      // none of them accept a body -- so these go in the query string. Sending them
      // as JSON meant the server silently fell back to defaults (full_refresh was
      // always False) or rejected the request outright on the required ones.
      const params = {};
      Object.entries(formattedData).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params[key] = value;
        }
      });

      const response = await axios({
        method: command.method,
        url: endpoint.startsWith('/api/') ? endpoint : `/api${endpoint}`,
        params
      });

      setCommandOutput(JSON.stringify(response.data, null, 2));
      toast.success('Command executed successfully');
    } catch (error) {
      console.error('Command execution error:', error);
      setCommandOutput(error.response?.data?.detail || error.message);
      toast.error('Command execution failed');
    } finally {
      setIsRunning(false);
    }
  };

  const renderFormField = (field) => {
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder={field.label}
            required={field.required}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={formData[field.name] || field.defaultValue || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            required={field.required}
          />
        );
      case 'date':
        return (
          <DatePicker
            selected={formData[field.name] || null}
            onChange={(date) => handleFieldChange(field.name, date)}
            dateFormat="yyyy-MM-dd"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholderText={field.label}
            required={field.required}
          />
        );
      case 'select':
        return (
          <select
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <div className="mt-1">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={formData[field.name] || false}
                onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <span className="ml-2">{field.label}</span>
            </label>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center">
        <Terminal className="h-8 w-8 text-blue-600 mr-3" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Commands</h1>
          <p className="text-gray-600">Execute system commands and operations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Command Selection */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Available Commands</h2>
          
          {Object.entries(commandTemplates).map(([key, command]) => (
            <div 
              key={key}
              className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                activeCommand === key 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => {
                setActiveCommand(key);
                setFormData({});
                setCommandOutput('');
              }}
            >
              <div className="flex items-center">
                <command.icon className="h-6 w-6 text-blue-600 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-900">{command.title}</h3>
                  <p className="text-sm text-gray-600">{command.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Command Form and Output */}
        <div className="space-y-6">
          {activeCommand && (
            <>
              {/* Command Form */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {commandTemplates[activeCommand].title}
                </h3>
                
                {commandTemplates[activeCommand].fields.length > 0 && (
                  <div className="space-y-4">
                    {commandTemplates[activeCommand].fields.map(field => (
                      <div key={field.name}>
                        <label className="block text-sm font-medium text-gray-700">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {renderFormField(field)}
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    This will execute on the server
                  </div>
                  <button
                    onClick={handleSubmit}
                    disabled={isRunning}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRunning ? (
                      <>
                        <div className="loading-spinner mr-2"></div>
                        Running...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Execute Command
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Command Output */}
              {commandOutput && (
                <div className="bg-gray-900 text-green-400 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <Terminal className="h-4 w-4 mr-2" />
                    <span className="text-sm font-medium">Command Output</span>
                  </div>
                  <pre className="text-sm whitespace-pre-wrap font-mono">
                    {commandOutput}
                  </pre>
                </div>
              )}
            </>
          )}

          {!activeCommand && (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Terminal className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Command</h3>
              <p className="text-gray-600">Choose a command from the list to configure and execute it.</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              setActiveCommand('sync-all');
              setFormData({});
            }}
            className="flex items-center justify-center px-4 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
          >
            <Database className="h-5 w-5 mr-2" />
            Sync All
          </button>
          <button
            onClick={() => {
              setActiveCommand('refresh-gold');
              setFormData({});
            }}
            className="flex items-center justify-center px-4 py-3 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
          >
            <Settings className="h-5 w-5 mr-2" />
            Refresh Gold
          </button>
          <button
            onClick={() => {
              setActiveCommand('cleanup-sync');
              setFormData({});
            }}
            className="flex items-center justify-center px-4 py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
          >
            <FileText className="h-5 w-5 mr-2" />
            Cleanup Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default Commands; 